import React from 'react';
import { oneOf, node, bool } from 'prop-types';
import cx from 'classnames';
import css from 'styled-jsx/css';
import breakpoints from '@lmig/lmds-tokens/dist/json/core/breakpoint.json';

const getBgColorStyles = color => {
  switch (color) {
    case 'coolWhite':
      return css.resolve`
        @import './global-styles/tokens';
        div {
          background-color: $lm-color-background-atmospheric;
        }
      `;
    case 'lightLibertyTeal':
      return css.resolve`
        @import './global-styles/tokens';
        div {
          background-color: $lm-color-palette-teal-10;
        }
      `;
    case 'safecoBlue':
      return css.resolve`
        div {
          background-color: #1a1446;
        }
      `;
    case 'transparent':
      return css.resolve`
        div {
          background-color: transparent;
        }
      `;
    case 'comparionLightGray':
      return css.resolve`
          div {
            background-color: #fafafa;
          }
        `;
    default:
      return css.resolve`
        @import './global-styles/tokens';
        div {
          background-color: $lm-color-form-input-background-default;
        }
      `;
  }
};

const getPaddingStyles = padding => {
  if (padding) {
    return css.resolve`
      @import './global-styles/tokens';
      $innerContainerPadding: 2rem;

      div {
        padding: $innerContainerPadding;
        @media screen and (min-width: ${breakpoints.lmig.breakpoint.md.min}) {
          padding: calc($innerContainerPadding / 2);
        }
      }
    `;
  }

  return css.resolve`
    div {
      padding: 0;
    }
  `;
};

const getShadowStyles = (shadow, shadowStyle) => {
  if (shadow && shadowStyle === 'default') {
    return css.resolve`
      div {
        box-shadow: 0px 1px 5px 0px rgba(0, 0, 0, 0.2), 0px 3px 1px -2px rgba(0, 0, 0, 0.12),
          0px 2px 2px 0px rgba(0, 0, 0, 0.14);
      }
    `;
  }

  if (shadow && shadowStyle === 'floating') {
    return css.resolve`
      div {
        box-shadow: 3px 6px 18px 6px rgba(0, 0, 0, 0.1);
      }
    `;
  }

  return css.resolve`
    div {
      box-shadow: none;
    }
  `;
};

const getBorderStyles = (border, rounded) => {

  if (rounded) {
    return css.resolve`
      div {
        border-top-right-radius: 1rem;
        border-bottom-right-radius: 1rem;
      }
    `;
  }

  if (border) {
    return css.resolve`
      div {
        border: solid 1px #c0bfc0;
      }
    `;
  }

  return css.resolve`
    div {
      border: none;
    }
  `;
};

const CardBase = ({
  children = undefined,
  backgroundColor = '',
  padding = false,
  shadow = false,
  shadowStyle = 'default',
  border = false,
  rounded = false,
  ...rest
}) => {
  const { className: bgColorClass, styles: bgColorStyles } = getBgColorStyles(backgroundColor);
  const { className: paddingClass, styles: paddingStyles } = getPaddingStyles(padding);
  const { className: shadowClass, styles: shadowStyles } = getShadowStyles(shadow, shadowStyle);
  const { className: borderClass, styles: borderStyles } = getBorderStyles(border, rounded);

  return (
    <div {...rest}>
      <div className={cx('inner', bgColorClass, paddingClass, shadowClass, borderClass)}>
        {children}
      </div>
      {bgColorStyles}
      {paddingStyles}
      {shadowStyles}
      {borderStyles}
      <style jsx>
        {`
          @import './global-styles/tokens';

          div {
            display: block;
            margin: 0;

            .inner {
              height: 100%;
              border-radius: inherit;
            }
          }
        `}
      </style>
    </div>
  );
};

CardBase.propTypes = {
  /**
    Background color of card
  */
  backgroundColor: oneOf(['', 'coolWhite', 'lightLibertyTeal', 'safecoBlue', 'comparionBlue']),
  children: node,
  /**
    2rem padding around card true or false
  */
  padding: bool,
  /**
    Card shadow using box-shadow: 3px 6px 18px 6px rgba(0, 0, 0, 0.1);
  */
  shadow: bool,
  /**
    Shadow style default or floating
  */
  shadowStyle: oneOf(['default', 'floating']),
  /**
    Border true or false
  */
  border: bool,
};

export default CardBase;
